import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import { Wedstrijddagen } from "@/features/wedstrijden/Wedstrijddagen";
import { keepScrollPosition } from "@jbnl/shared-ui";

export function WedstrijddagenPage() {
  const { t } = useTranslation(["navigatie", "common"]);
  const { data: me, isPending } = useMe();
  const { data: seizoenen } = useSeizoenen();
  const {
    seizoen: seizoenId,
    dag: dagId,
    pagina,
    tab,
  } = useSearch({ from: "/wedstrijddagen" });
  const navigate = useNavigate({ from: "/wedstrijddagen" });

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  // A seizoen named in the URL wins; otherwise the one last picked on the Seizoenen screen;
  // otherwise the running one. An id that no longer exists is a page with nothing chosen, so it
  // falls back the same way rather than answering not-found.
  const seizoen = kiesSeizoen(seizoenen, seizoenId);

  return (
    <Shell name={me.name} wide>
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("paginas.wedstrijddagen.label")}
        </h1>
        {seizoen ? (
          <p className="text-sm text-muted-foreground">
            {t("common:seizoen.lopend", { naam: seizoen.name })}
            {t("common:seizoen.andereBefore")}
            <Link className="jb-link" to="/seizoenen">
              {t("common:seizoen.link")}
            </Link>
            {t("common:seizoen.na")}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("common:seizoen.geenBefore")}
            <Link className="jb-link" to="/seizoenen">
              {t("common:seizoen.link")}
            </Link>
            {t("common:seizoen.na")}
          </p>
        )}
      </header>

      {seizoen && (
        <Wedstrijddagen
          seizoen={seizoen}
          dagId={dagId}
          onKies={(id) =>
            void navigate({
              ...keepScrollPosition,
              search: (previous) => ({
                ...previous,
                seizoen: seizoen.id,
                dag: id,
              }),
            })
          }
          tab={tab}
          // `replace`, not `push`: below `xl` the detail pane is reached by a navigation, so
          // pushing a section would make Back walk the tabs before returning to the list.
          onKiesTab={(next) =>
            void navigate({
              ...keepScrollPosition,
              replace: true,
              search: (previous) => ({ ...previous, tab: next }),
            })
          }
          pagina={pagina}
          onPaginaChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, pagina: next }),
              ...keepScrollPosition,
            })
          }
        />
      )}
    </Shell>
  );
}
